/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// redux
import { useDispatch, useSelector } from 'react-redux';
import {
  PRODUCT_ARTICLE_SENTIMENT,
  PRODUCT_COMMENTING,
  PRODUCT_REQUEST_PENDING,
  PRODUCT_TRENDING_NOW,
  sendTrendingNowAccessRequest,
  updateTrendingNowConfiguration
} from "../../../../../state/thunks/engagement";
// material
import { Button, InlineMessage } from '@insticator/insticator-ui';
import ExpandableSection from '../../../../../material/expandablesection/expandablesection';
import ConfigController from '../../../../../material/expandablesection/controllers/configcontroller/configcontroller';
import GeneralConfigs, { TRENDING_TITLE_MAX_LENGTH } from './general/general';
import MarketingPage from './marketing/marketing';
import ScrollWithAdsModal from './modal/modal'
// styles
import injectSheet from 'react-jss';
import styles from './trending_styles';
import { getTestPage } from "../../../../../state/thunks/api/tnu"


/* ========== ~~~~~~~~~~ TRENDING CONFIG ~~~~~~~~~~ ========== */
const TrendingConfig = injectSheet(styles)((props) => {
  const dispatch = useDispatch();
  const {
    engagement,
    session: {
      siteUUID,
      accountUUID,
      mocked,
    },
    engagement: {
      isLoading,
      accountProducts,
      trendingNowUnitUUID,
      trendingNowAccessRequest: {
        requestStatus,
      } = {},
    }
  } = useSelector((state) => state);

  const [isOpened, setIsOpened] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [config, setConfig] = useState(engagement);
  const [defaultConfig, setDefaultConfig] = useState(engagement);
  const [modalIsOpened, setModalIsOpened] = useState(false)

  const hasArticleSentimentAccess = accountProducts.includes(PRODUCT_ARTICLE_SENTIMENT)
  const hasCommentingAccess = accountProducts.includes(PRODUCT_COMMENTING)

  const hasTrendingNowAccess = useMemo(() =>
      accountProducts.includes(PRODUCT_TRENDING_NOW)
    , [accountProducts]
  )

  useEffect(() => {
    setConfig(engagement)
    setDefaultConfig(engagement)
  }, [engagement])

  const handleToggle = useCallback(() => {
    setIsOpened(!isOpened)
  }, [isOpened]);

  const handleCancel = useCallback(() => {
    setIsOpened(false);
    setIsChanged(false);
    setConfig(defaultConfig)
  }, [defaultConfig]);

  const handleSave = useCallback(async () => {
    const unitConfig = {
      ...config,
      commenting_enabled : hasCommentingAccess,
      sentiment_enabled : hasArticleSentimentAccess
    }
    setIsOpened(false)
    await dispatch(updateTrendingNowConfiguration({...unitConfig, siteUUID}))
    setIsChanged(false)
  }, [config, siteUUID, dispatch]);

  const handleChangeTitle = useCallback((e) => {
    setConfig({
      ...config,
      titleTrending: e.target.value.substring(0, TRENDING_TITLE_MAX_LENGTH),
    })
    setIsChanged(true)
  }, [config])

  const handleChange = useCallback((changedValue) => {
    setConfig({
      ...config,
      ...changedValue
    })
    setIsChanged(true)
  }, [config])

  const handleChangeView = useCallback((changedValue) => {
    if (changedValue.display !== 'SCROLL_WITH_AD' && !modalIsOpened) {
      setConfig({
        ...config,
        ...changedValue,
        // This is needed to set default value on display change
        // 10 is default for flat view, 1 is default for rail view
        visible_amount: changedValue.display === 'FLAT' ? 10 : 1
      })
      setIsChanged(true)
    } else {
      setModalIsOpened(true)
    }
  }, [config, modalIsOpened])

  const handleChangeImage = useCallback((changedValue) => {
    if (config.isCustomImage === 'CUSTOM'){
      setConfig({
        ...config,
        ...changedValue
      })
    }
    setIsChanged(true)
  }, [config])

  const handleChangePlaceholderImages = useCallback((changedValue) => {
    if (changedValue.isCustomImage === 'CUSTOM') {
      setConfig({
        ...config,
        ...changedValue
      })
    }
    // Cleanup images if default is selected
    else {
      setConfig({
        ...config,
        ...changedValue,
        placeholder_img_file: '',
        placeholder_img_url: ''
      })
    }
    setIsChanged(true)
  }, [config])

  const handleGetAccess = useCallback(() => {
    if (hasTrendingNowAccess) return;
    dispatch(sendTrendingNowAccessRequest(accountUUID))
  }, [dispatch, hasTrendingNowAccess, accountUUID])

  const testPageUrl = useMemo(() =>
      trendingNowUnitUUID
        ? getTestPage({trendingNowUnitUUID})
        : null
    , [trendingNowUnitUUID])

  const handleOpenTestPage = useCallback(() =>
      window.open(testPageUrl, '_blank').focus()
    , [testPageUrl])

  const controller = (
    <ConfigController
      handleToggle={handleToggle}
      handleCancel={handleCancel}
      handleSave={hasTrendingNowAccess ? handleSave : null}
      isOpened={isOpened}
      isChanged={isChanged && !modalIsOpened}
      isLoading={isLoading}
      title='Trending Now'
      description={mocked && hasTrendingNowAccess && testPageUrl
        ? <Button
            variant={'system'}
            text={'Test page'}
            type={'action'}
            handleClick={handleOpenTestPage}
          />
        : ''
      }
    />
  );
  return (

  /*
  For access to different sections you must change mocked data on
  'src/state/slices/engagement.js'
  */

    <ExpandableSection
      className={`trendingconfig ${props.classes.trendingconfig}`}
      controller={controller}
      handleCancel={handleCancel}
      handleSave={hasTrendingNowAccess ? handleSave : null}
      isOpened={isOpened}
      isLoading={isLoading}
      isChanged={isChanged && !modalIsOpened}
    >
      {isOpened && <>
        {requestStatus === PRODUCT_REQUEST_PENDING && <>
          <InlineMessage
            name={'trendingmessage'}
            variant={'none'}
            icon={'exclamation-triangle'}
            message={'Please wait while we are processing your request.'}
            display={true}
            title={''}
            close={false}
          />
        </>}

        {!hasTrendingNowAccess && requestStatus !== PRODUCT_REQUEST_PENDING && <MarketingPage onRequest={handleGetAccess} />}
        {modalIsOpened && <ScrollWithAdsModal closeModal={() => setModalIsOpened(false)}/>}
        {hasTrendingNowAccess && <>
          <GeneralConfigs
            handleChangeTitle={handleChangeTitle}
            handleChangeTimeRange={handleChange}
            handleChangeSelectArticles={handleChange}
            handleChangeDisplayView={handleChangeView}
            handleChangeAmountArticles={handleChange}
            handleChangePlaceholderImages={handleChangePlaceholderImages}
            handleUploadImage={handleChangeImage}
            config={config}
            modalIsOpened={modalIsOpened}
          />
        </>}
      </>}
    </ExpandableSection>
  );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default TrendingConfig;
